import { AnalyticsEvent, AnalyticsEventType, AnalyticsPayload, Audience, AIPlatform } from './types';

/**
 * Analytics tracking for Explore with AI interactions
 * Implements Phase 2 analytics specification
 */
export class Analytics {
  private onAnalytics?: (event: AnalyticsEvent) => void;
  private sessionId: string;
  private enabled: boolean;

  constructor(onAnalytics?: (event: AnalyticsEvent) => void, enabled: boolean = true) {
    this.onAnalytics = onAnalytics;
    this.enabled = enabled;
    this.sessionId = this.generateSessionId();
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get page context information
   */
  private getPageContext() {
    if (typeof window === 'undefined') return {};
    
    return {
      pageUrl: window.location.href,
      pageTitle: document.title,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      screenResolution: `${screen.width}x${screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Track an analytics event with comprehensive context
   */
  track(eventType: AnalyticsEventType, payload: AnalyticsPayload, configId?: string): void {
    if (!this.enabled) return;

    const event: AnalyticsEvent = {
      event: eventType,
      sessionId: this.sessionId,
      timestamp: Date.now(),
      properties: {
        ...payload,
        ...this.getPageContext()
      },
      configId
    };

    // Call custom analytics handler if provided
    if (this.onAnalytics) {
      try {
        this.onAnalytics(event);
      } catch (error) {
        console.error('Analytics handler error:', error);
      }
    }

    // Default console logging for development
    console.log('Analytics event:', eventType, event.properties);

    // Emit custom DOM event for external tracking
    if (typeof window !== 'undefined') {
      const customEvent = new CustomEvent('explore-with-ai:analytics', {
        detail: event,
        bubbles: true
      });
      window.dispatchEvent(customEvent);
    }

    // Store in sessionStorage for debugging
    try {
      const events = JSON.parse(sessionStorage.getItem('faathum-analytics') || '[]');
      events.push(event);
      // Keep only last 50 events
      if (events.length > 50) events.shift();
      sessionStorage.setItem('faathum-analytics', JSON.stringify(events));
    } catch (error) {
      // Ignore storage errors
    }
  }

  /**
   * Track widget open event
   */
  trackOpen(datasetId: string, location?: string): void {
    this.track('explore_ai_open', {
      datasetId,
      location: location || window?.location?.href
    });
  }

  /**
   * Track mode selection
   */
  trackModeSelected(datasetId: string, mode: string): void {
    this.track('explore_ai_mode_selected', {
      datasetId,
      mode
    });
  }

  /**
   * Track audience selection
   */
  trackAudienceSelected(datasetId: string, audience: Audience): void {
    this.track('explore_ai_audience_selected', {
      datasetId,
      audience
    });
  }

  /**
   * Track platform selection
   */
  trackPlatformSelected(datasetId: string, platform: AIPlatform, mode?: string, audience?: Audience): void {
    this.track('explore_ai_platform_selected', {
      datasetId,
      platform,
      mode,
      audience
    });
  }

  /**
   * Track prompt copy action
   */
  trackPromptCopied(datasetId: string, contextPresent: boolean): void {
    this.track('explore_ai_copy_prompt', {
      datasetId,
      contextPresent
    });
  }

  /**
   * Track AI platform launch
   */
  trackLaunch(datasetId: string, platform: AIPlatform, mode: string, audience?: Audience, contextPresent?: boolean): void {
    this.track('explore_ai_launch', {
      datasetId,
      platform,
      mode,
      audience,
      contextPresent
    });
  }

  /**
   * Track prompt editing
   */
  trackPromptEdited(datasetId: string): void {
    this.track('explore_ai_prompt_edited', {
      datasetId,
      promptEdited: true
    });
  }
}

/**
 * Default analytics instance for simple usage
 */
export const analytics = new Analytics();
